import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { FPAData, FPADataFactory, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { ListProjectItemRequest } from '../../../../services/soap/item/requests/ListProjectItemRequest';
import { ProjectItem } from '../../../../services/soap/item/requests/ProjectItem';
import { ListProjectItemRequestOptions_ProjectItem } from '../../../../services/soap/item/requests/ListProjectItemRequestOptions';
import { CreateProjectItemRequest } from '../../../../services/soap/item/requests/CreateProjectItemRequest';
import { ListActivityTemplateRequest } from '../../../../services/soap/project/requests/ListActivityTemplateRequest';
import { CreateActivityRequest } from '../../../../services/soap/project/requests/CreateActivityRequest';
import { Activity } from '../../../../services/soap/dataObjects/Activity';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { Key } from '@adobe/react-spectrum';
import { GetActivityTemplateRequest } from '../../../../services/soap/project/requests/GetActivityTemplateRequest';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { GetFolderProjectTypeRequest } from '../../../../services/soap/project/requests/GetFolderProjectTypeRequest';
import { ActivityTemplate as SubActivityTemplate } from '../../../../services/soap/project/responses/GetFolderProjectTypeResponse';
import { ActivityTemplate } from '../../../../services/soap/project/responses/GetActivityTemplateResponse';
import { DateValue } from '@internationalized/date';
import { ListComponentCategoriesRequest } from '../../../../services/soap/item/requests/ListComponentCategoriesRequest';
import { ComponentCategory } from '../../../../services/soap/item/responses/ListComponentCategoriesResponse';
import { ContextFieldsMapping } from '../../Context/ContextFieldsMapping';
import { useActivityFormInitialization } from '../../../../hooks/useActivityFormInitialization';

export interface IUseAddMultipleActivitiesDialogProps {
  masterActivity: FPAData;
  onClose: (reload: boolean) => void;
  selectedContext?: string;
}

export function useAddMultipleActivitiesDialog({ masterActivity, onClose, selectedContext = '0' }: IUseAddMultipleActivitiesDialogProps) {
  const { store, projectService, itemService } = useDependency();
  const { t } = useTranslation();
  const { getDefaultValues, persistFormValues } = useActivityFormInitialization();

  type ItemId = string;

  interface PrimaryItemHierarchy {
    primaryProjectItem: ProjectItem;
    primaryItemId: ItemId;
    nodesInCreationOrder: ActivityItemPlanNode[];
  }

  interface ActivityItemPlanNode {
    itemId: ItemId;
    parentItemId: ItemId | null;
    sourceProjectItem: ProjectItem;
  }

  interface ActivityCreationPlan {
    primaryProjectItem: ProjectItem;
    primaryItemId: ItemId;
    nodesInCreationOrder: ActivityItemPlanNode[];
  }

  const [name, setName] = useState('');
  const [dateFrom, setDateFrom] = useState<DateValue | null>(null);
  const [dateTo, setDateTo] = useState<DateValue | null>(null);
  const [servicedItemsList, setServicedItemsList] = useState<ProjectItem[]>([]);
  const [selectedServicedItemKeys, setSelectedServicedItemKeys] = useState<Set<Key>>(new Set());
  const [activityTemplates, setActivityTemplates] = useState<SubActivityTemplate[]>([]);
  const [selectedActivityType, setSelectedActivityType] = useState<number | null>(null);
  const [initialStatus, setInitialStatus] = useState<string | undefined>(undefined);
  const [selectedActivityTemplateFormId, setSelectedActivityTemplateFormId] = useState<string | undefined>(undefined);
  const [masterActivityContext, setMasterActivityContext] = useState<FPAData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [componentCategories, setComponentCategories] = useState<ComponentCategory[]>([]);

  const _t = (key: string, params?: Record<string, any>) => t(key, { ns: 'layout_components', ...params });
  
  // Debounce search query with 400ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load serviced items from all customer projects
  const loadServicedItems = useCallback(async () => {
    setLoading(true);
    try {
      let requestOptions: any = {};
      
      // If context is selected and not 'All' (0), use context-specific filtering
      if (selectedContext && selectedContext !== '0' && masterActivityContext.length > 0) {
        const selectedContextData = masterActivityContext.find(ctx => +ctx.id === +selectedContext);
        if (selectedContextData) {
          // Use ListProjectItemRequestOptions_ProjectItem for context-specific filtering
          // Using id as both folder and project since Context2 doesn't have separate folder/project properties
          const options = new ListProjectItemRequestOptions_ProjectItem(
            selectedContextData.type === FPADataTypes.FOLDER ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.PROJECT ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.ACTIVITY ? selectedContextData.id.toString() : '',
            ContextFieldsMapping.PROJECT_ITEMS_TYPE.TECHNOLOGY_ITEMS
          );
          requestOptions = options;
        }
      }
      
      const response = await itemService.listProjectItem(
        new ListProjectItemRequest(store.Server, store.SessionId, 10, 0, requestOptions)
      );
      
      if (response.result === 'OK' && response.ROWS && response.ROWS.length > 0) {
        const items = response.ROWS.map(row => row.ITEM);
        setServicedItemsList(items);
      } else {
        setServicedItemsList([]);
      }
    } catch (error) {
      console.error('Error loading serviced items:', error);
      ToastQueue.negative(t('error_loading_serviced_items', { ns: 'layout_components' }), { timeout: 3000 });
      setServicedItemsList([]);
    } finally {
      setLoading(false);
    }
  }, [itemService, store.Server, store.SessionId, selectedContext, masterActivityContext, t]);

  // Fetch specific activity template by ID
  const fetchActivityTemplateById = useCallback(async (templateId: number) => {
    const response = await projectService.getActivityTemplate(
      new GetActivityTemplateRequest(store.Server, store.SessionId, templateId)
    );
    return response;
  }, [projectService, store.Server, store.SessionId]);

  // Fetch folder project type and its activity templates
  const fetchFolderProjectType = useCallback(async (activityTemplateType: number) => {
    const response = await projectService.getFolderProjectType(
      new GetFolderProjectTypeRequest(store.Server, store.SessionId, activityTemplateType)
    );
    return response;
  }, [projectService, store.Server, store.SessionId]);

  // Load activity templates
  const loadActivityTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const folderProjectTypeResp = await fetchFolderProjectType(masterActivity.item_type_id);
      
      if (
        folderProjectTypeResp &&
        folderProjectTypeResp.result === 'OK' &&
        folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES &&
        folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES.TEMPLATE.length > 0
      ) {
        const sub_activity_templates = folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES.TEMPLATE as SubActivityTemplate[];
        setActivityTemplates(sub_activity_templates);
        
        // Auto-select if only one sub-template exists
        if (sub_activity_templates.length === 1) {
          setSelectedActivityType(sub_activity_templates[0].activityType);
          setName(sub_activity_templates[0].activityTemplateName);
          
          var sub_activity_template = await fetchActivityTemplateById(sub_activity_templates[0].activityTemplate);
          if (sub_activity_template.TEMPLATE.initialStatus) {
            setInitialStatus(sub_activity_template.TEMPLATE.initialStatus.toString());
          }
          if (sub_activity_template.TEMPLATE.form) {
            setSelectedActivityTemplateFormId(sub_activity_template.TEMPLATE.form);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching folder project type for activity template:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchActivityTemplateById, fetchFolderProjectType, masterActivity.item_type_id]);

  // Load master activity context
  const loadMasterActivityContext = useCallback(async () => {
    try {
      const response = await projectService.getContextPath2(
        new GetContextPathRequest(store.Server, store.SessionId, masterActivity.id)
      );

      if (response.result === 'OK') {
        setMasterActivityContext(response.CONTEXT.map( context => FPADataFactory.createFromContextItem2(context)));
      } else {
        setMasterActivityContext([]);
      }
    } catch (error) {
      console.error('Error loading master activity context:', error);
      setMasterActivityContext([]);
    }
  }, [projectService, store.Server, store.SessionId, masterActivity.id]);

  // Load component categories once and cache them locally
  const loadComponentCategories = useCallback(async () => {
    try {
      const request = new ListComponentCategoriesRequest(store.Server, store.SessionId, {});
      const response = await itemService.listComponentCategories(request);

      const categories = Array.isArray(response.COMPONENT_CATEGORY) ? response.COMPONENT_CATEGORY : [];
      setComponentCategories(categories);
    } catch (error) {
      console.error('Error loading component categories:', error);
      ToastQueue.negative(t('error_loading_component_categories', { ns: 'layout_components' }), { timeout: 3000 });
      setComponentCategories([]);
    }
  }, [itemService, store.Server, store.SessionId, t]);

  // Filter serviced items on the frontend based on search query
  const filteredServicedItemsList = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return servicedItemsList;
    }
    
    const query = debouncedSearchQuery.trim().toLowerCase();
    return servicedItemsList.filter(item => {
      const name = (item.name || '').toLowerCase();
      const refId = (item.refId || '').toLowerCase();
      const itemSerial = (item.itemSerial || '').toLowerCase();
      
      return name.includes(query) || refId.includes(query) || itemSerial.includes(query);
    });
  }, [servicedItemsList, debouncedSearchQuery]);

  // Map component category ID to human-readable name for O(1) lookup
  const componentCategoryMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};

    for (const category of componentCategories) {
      if (category.id) {
        map[category.id] = category.name ?? category.id;
      }
    }

    return map;
  }, [componentCategories]);

  // Initialize data on mount
  useEffect(() => {
    loadServicedItems();
    loadActivityTemplates();
    loadMasterActivityContext();
    loadComponentCategories();
  }, []); // Empty dependency array to run only once on mount

  // Reload serviced items when context changes
  useEffect(() => {
    if (masterActivityContext.length > 0) {
      loadServicedItems();
    }
  }, [selectedContext, masterActivityContext.length, loadServicedItems]);

  // Handle activity type change - fetches initial status for selected type
  const handleActivityTypeChange = useCallback(async (activityTypeId: number) => {
    setSelectedActivityType(activityTypeId);
    
    // Find the selected template
    const selectedTemplate = activityTemplates.find(template => template.activityType === activityTypeId);
    
    if (selectedTemplate) {
      try {
        setName(selectedTemplate.activityTemplateName);
        const activityTemplate = await fetchActivityTemplateById(selectedTemplate.activityTemplate);
        if (activityTemplate.TEMPLATE.initialStatus) {
          setInitialStatus(activityTemplate.TEMPLATE.initialStatus.toString());
        } else {
          setInitialStatus(undefined);
        }
        if (activityTemplate.TEMPLATE.form) {
          setSelectedActivityTemplateFormId(activityTemplate.TEMPLATE.form);
        } else {
          setSelectedActivityTemplateFormId(undefined);
        }
      } catch (error) {
        console.error('Error fetching activity template for initial status:', error);
        setInitialStatus(undefined);
        setSelectedActivityTemplateFormId(undefined);
      }
    }
  }, [activityTemplates, fetchActivityTemplateById]);

  // Get selected serviced items
  const getSelectedServicedItems = useCallback((): ProjectItem[] => {
    return servicedItemsList.filter(item => selectedServicedItemKeys.has(item.id!));
  }, [servicedItemsList, selectedServicedItemKeys]);

  // Build relationship-aware hierarchies for selected serviced items using parentId from batch-fetched project items
  const buildHierarchiesForSelectedItems = useCallback(
    async (selectedItems: ProjectItem[]): Promise<PrimaryItemHierarchy[]> => {
      if (!selectedItems.length) {
        return [];
      }

      // Derive request options from context (same logic as loadServicedItems)
      let requestOptions: any = {};
      if (selectedContext && selectedContext !== '0' && masterActivityContext.length > 0) {
        const selectedContextData = masterActivityContext.find(
          (ctx) => +ctx.id === +selectedContext
        );
        if (selectedContextData) {
          requestOptions = new ListProjectItemRequestOptions_ProjectItem(
            selectedContextData.type === FPADataTypes.FOLDER ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.PROJECT ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.ACTIVITY ? selectedContextData.id.toString() : '',
            ContextFieldsMapping.PROJECT_ITEMS_TYPE.TECHNOLOGY_ITEMS
          );
        }
      }

      const response = await itemService.listProjectItem(
        new ListProjectItemRequest(store.Server, store.SessionId, 500, 0, requestOptions)
      );

      if (response.result !== 'OK' || !response.ROWS || response.ROWS.length === 0) {
        // Fallback: return each selected item as a root-only hierarchy
        return selectedItems.map((item) => ({
          primaryProjectItem: item,
          primaryItemId: (item.id as ItemId) ?? '',
          nodesInCreationOrder: [
            {
              itemId: (item.id as ItemId) ?? '',
              parentItemId: null,
              sourceProjectItem: item,
            },
          ],
        }));
      }

      const projectItems = response.ROWS.map((row) => row.ITEM);
      const projectItemById = new Map<string, ProjectItem>();
      for (const pi of projectItems) {
        if (pi.id) {
          projectItemById.set(pi.id, pi);
        }
      }

      const childrenByParentId = projectItems.reduce((map, item) => {
        const pid = item.parentId && item.parentId !== '' ? item.parentId : null;
        if (pid) {
          const arr = map.get(pid) ?? [];
          arr.push(item);
          map.set(pid, arr);
        }
        return map;
      }, new Map<string, ProjectItem[]>());

      const hierarchies: PrimaryItemHierarchy[] = [];

      for (const selectedItem of selectedItems) {
        const primaryItemId: ItemId = (selectedItem.id as ItemId | undefined) ?? '';

        if (!primaryItemId) {
          hierarchies.push({
            primaryProjectItem: selectedItem,
            primaryItemId: '',
            nodesInCreationOrder: [
              { itemId: '', parentItemId: null, sourceProjectItem: selectedItem },
            ],
          });
          continue;
        }

        // BFS from root to collect root + all descendants (multi-level nesting)
        const nodesInCreationOrder: ActivityItemPlanNode[] = [];
        const visited = new Set<string>();
        const queue: ProjectItem[] = [selectedItem];
        const selectedItemInBatch = projectItemById.get(primaryItemId) ?? selectedItem;

        nodesInCreationOrder.push({
          itemId: primaryItemId,
          parentItemId: null, // Root of this hierarchy
          sourceProjectItem: selectedItemInBatch,
        });
        visited.add(primaryItemId);

        while (queue.length > 0) {
          const current = queue.shift()!;
          const currentId = (current.id as ItemId) ?? '';
          const children = childrenByParentId.get(currentId) ?? [];

          for (const child of children) {
            const childId = (child.id as ItemId) ?? '';
            if (!childId || visited.has(childId)) continue;
            visited.add(childId);

            const parentId = child.parentId && child.parentId !== '' ? child.parentId : null;
            nodesInCreationOrder.push({
              itemId: childId,
              parentItemId: parentId,
              sourceProjectItem: child,
            });
            queue.push(child);
          }
        }

        hierarchies.push({
          primaryProjectItem: selectedItem,
          primaryItemId,
          nodesInCreationOrder,
        });
      }

      return hierarchies;
    },
    [itemService, store.Server, store.SessionId, selectedContext, masterActivityContext]
  );

  // Create a serviced item (type=4) from a source ProjectItem
  const createServicedItemFromSource = useCallback(async (
    sourceItem: ProjectItem,
    activityId: string,
    targetParentId?: string
  ): Promise<string | null> => {
    try {
      const newItem: ProjectItem = {
        name: sourceItem.name,
        item: sourceItem.item,
        itemSerial: sourceItem.itemSerial,
        quantity: sourceItem.quantity,
        totalQuantity: sourceItem.totalQuantity,
        amount: sourceItem.amount,
        catalogPrice: sourceItem.catalogPrice,
        totalAmount: sourceItem.totalAmount,
        totalAmountWithVat: sourceItem.totalAmountWithVat,
        currency: sourceItem.currency,
        purchaseCurrency: sourceItem.purchaseCurrency,
        vatValue: sourceItem.vatValue,
        purchasePrice: sourceItem.purchasePrice,
        attrib0: sourceItem.attrib0,
        attrib1: sourceItem.attrib1,
        attrib2: sourceItem.attrib2,
        supplier: sourceItem.supplier,
        refId: sourceItem.refId,
        note: sourceItem.note,
        folder: sourceItem.folder,
        project: sourceItem.project,
        activity: activityId,
        parentId: targetParentId,
        type: ContextFieldsMapping.PROJECT_ITEMS_TYPE.SERVICED_ITEMS
      };

      const response = await itemService.createProjectItem(
        new CreateProjectItemRequest(store.Server, store.SessionId, newItem)
      );

      if (response.result === 'OK' && response.itemId) {
        return response.itemId;
      }
      return null;
    } catch (error) {
      console.error('Error creating serviced item from source:', error);
      return null;
    }
  }, [itemService, store.Server, store.SessionId]);

  const buildActivityCreationPlans = useCallback(
    async (selectedItems: ProjectItem[]): Promise<ActivityCreationPlan[]> => {
      if (!selectedItems.length) {
        return [];
      }

      const hierarchies = await buildHierarchiesForSelectedItems(selectedItems);

      return hierarchies.map((h) => ({
        primaryProjectItem: h.primaryProjectItem,
        primaryItemId: h.primaryItemId,
        nodesInCreationOrder: h.nodesInCreationOrder,
      }));
    },
    [buildHierarchiesForSelectedItems]
  );

  // Validate form
  const validateForm = useCallback((): { isValid: boolean; errorMessage?: string } => {
    if (!name.trim()) {
      return { isValid: false, errorMessage: _t('name_is_required') };
    }

    if (selectedServicedItemKeys.size === 0) {
      return { isValid: false, errorMessage: _t('select_at_least_one_serviced_item') };
    }

    if (activityTemplates.length > 1 && !selectedActivityType) {
      return { isValid: false, errorMessage: _t('activity_type_is_required') };
    }

    return { isValid: true };
  }, [name, selectedServicedItemKeys.size, activityTemplates.length, selectedActivityType]);

  // Create multiple activities
  const createActivities = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      ToastQueue.negative(validation.errorMessage || '', { timeout: 3000 });
      return;
    }

    setSaving(true);
    const selectedItems = getSelectedServicedItems();
    let successCount = 0;
    let errorCount = 0;
    let itemsTransferredCount = 0;
    let itemsFailedCount = 0;

    try {
      const plans = await buildActivityCreationPlans(selectedItems);

      const activityTypeForForm = selectedActivityType ?? activityTemplates[0]?.activityType;
      let defaultValues: Record<string, any> = {};
      if (activityTypeForForm != null) {
        try {
          defaultValues = await getDefaultValues(activityTypeForForm, null, selectedActivityTemplateFormId);
        } catch (err) {
          console.error('Error loading custom form default values for bulk create:', err);
        }
      }

      for (const plan of plans) {
        const { primaryProjectItem, nodesInCreationOrder } = plan;

        try {
          const activity: Activity = {
            name: name,
            startDate: dateFrom ? new Date(dateFrom.year, dateFrom.month - 1, dateFrom.day) : undefined,
            finalDate: dateTo ? new Date(dateTo.year, dateTo.month - 1, dateTo.day) : undefined,
            activityType: selectedActivityType?.toString(),
            folder: primaryProjectItem.folder,
            project: primaryProjectItem.project,
            masterActivity: masterActivity.id.toString(),
            // refId: primaryProjectItem.refId,
            customState: initialStatus,
            formId: selectedActivityTemplateFormId || '',
          };

          const response = await projectService.createActivity(
            new CreateActivityRequest(store.Server, store.SessionId, activity)
          );

          if (response.result === 'OK' && response.newId) {
            successCount++;
            const newActivityId = response.newId.toString();

            if (Object.keys(defaultValues).length > 0) {
              try {
                const createdActivity = await projectService.getActivity(
                  new GetActivityRequest(store.Server, store.SessionId, response.newId)
                );
                if (createdActivity?.ACTIVITY?.formValues) {
                  const persisted = await persistFormValues(
                    +createdActivity.ACTIVITY.formValues,
                    defaultValues
                  );
                  if (!persisted) {
                    console.error(
                      `Failed to persist custom form defaults for activity ${newActivityId}`
                    );
                  }
                }
              } catch (err) {
                console.error(
                  `Error persisting custom form defaults for activity ${newActivityId}:`,
                  err
                );
              }
            }

            // Map of underlying itemId -> created project item ID within this activity
            const createdProjectItemIdByItemId = new Map<ItemId, string>();

            for (const node of nodesInCreationOrder) {
              const parentProjectItemId = node.parentItemId
                ? createdProjectItemIdByItemId.get(node.parentItemId)
                : undefined;

              try {
                const createdItemId = await createServicedItemFromSource(
                  node.sourceProjectItem,
                  newActivityId,
                  parentProjectItemId
                );

                if (createdItemId) {
                  itemsTransferredCount++;
                  if (node.itemId) {
                    createdProjectItemIdByItemId.set(node.itemId, createdItemId);
                  }
                } else {
                  itemsFailedCount++;
                  console.error(
                    `Failed to transfer item ${node.sourceProjectItem.name} to activity ${newActivityId}`
                  );
                }
              } catch (error) {
                itemsFailedCount++;
                console.error(
                  `Error transferring item ${node.sourceProjectItem.name} to activity ${newActivityId}:`,
                  error
                );
              }
            }
          } else {
            errorCount++;
            console.error(
              `Failed to create activity for item ${primaryProjectItem.name}:`,
              response
            );
          }
        } catch (error) {
          errorCount++;
          console.error(
            `Error creating activity for item ${primaryProjectItem.name}:`,
            error
          );
        }
      }

      // Show summary toast
      if (successCount > 0) {
        ToastQueue.positive(
          _t('activities_created_successfully', { count: successCount }),
          { timeout: 3000 }
        );
      }

      if (errorCount > 0) {
        ToastQueue.negative(
          _t('activities_creation_failed', { count: errorCount }),
          { timeout: 3000 }
        );
      }

      if (itemsTransferredCount > 0) {
        ToastQueue.positive(
          _t('items_transferred_successfully', { count: itemsTransferredCount }),
          { timeout: 3000 }
        );
      }

      if (itemsFailedCount > 0) {
        ToastQueue.negative(
          _t('items_transfer_failed', { count: itemsFailedCount }),
          { timeout: 3000 }
        );
      }

      // Close dialog and reload if any activities were created
      if (successCount > 0) {
        onClose(true);
      }
    } catch (error) {
      console.error('Error in createActivities:', error);
      ToastQueue.negative(_t('error_creating_activities'), { timeout: 3000 });
    } finally {
      setSaving(false);
    }
  }, [
    validateForm,
    getSelectedServicedItems,
    name,
    dateFrom,
    dateTo,
    selectedActivityType,
    initialStatus,
    selectedActivityTemplateFormId,
    masterActivity.id,
    projectService,
    store.Server,
    store.SessionId,
    onClose,
    _t,
    buildActivityCreationPlans,
    createServicedItemFromSource,
    activityTemplates,
    getDefaultValues,
    persistFormValues,
  ]);

  return {
    name,
    setName,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    servicedItemsList: filteredServicedItemsList,
    selectedServicedItemKeys,
    setSelectedServicedItemKeys,
    activityTemplates,
    selectedActivityType,
    setSelectedActivityType,
    handleActivityTypeChange,
    masterActivityContext,
    loading,
    saving,
    createActivities,
    validateForm,
    searchQuery,
    setSearchQuery,
    componentCategoryMap,
  };
}

